/**
 * NeoCoreConciliacionMensualPage — conciliación bancaria por mes (ADR 022).
 *
 * Contabilidad (Carlitos) elige cuenta + mes, sube el estado de cuenta del banco
 * (Interbanco .xls · BAC "Consulta de Transferencias" .csv) y ve el cruce contra el
 * sistema: retiros, rebotes, comisiones, ingresos y otros movimientos. Exporta a Excel.
 *
 * Backend: importar_movimientos_bancarios · conciliacion_mes (migración 0374).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as XLSX from 'xlsx'
import { supabase } from '@/lib/supabase'
import { formatQ } from '@/lib/format'
import { parseArchivoBancario } from '@/lib/conciliacion/parsers'
import { ConsolidadoMes } from './NeoCoreConciliacionConsolidado'

type Cuenta = { id: string; banco: string; numero_cuenta: string; alias: string | null; activa: boolean }
type Retiro = {
  estado: string; fecha_banco: string; doc: string | null; monto: number; beneficiario_banco: string | null
  descripcion: string; retiro_id: string | null; neo_id: string | null; nombre_sistema: string | null
  fecha_sistema: string | null; cuenta_salida_sistema: string | null; comision: number | null; reversa_doc: string | null
}
type SinBanco = { retiro_id: string; neo_id: string; nombre: string; monto: number; fecha_sistema: string; cuenta_salida_sistema: string }
type Ingreso = {
  estado: string; fecha_banco: string; doc: string | null; monto: number; origen_nombre: string | null
  origen_cuenta: string | null; descripcion: string; neo_id?: string; nombre?: string; tipo?: string; fecha_sistema?: string
}
type Otro = { fecha_banco: string; tipo: string; categoria: string; doc: string | null; monto: number; descripcion: string; contraparte: string | null }
type Resultado = {
  cuenta: { banco: string; numero: string; contable: string | null }
  mes: string
  cargado: { movimientos: number; desde: string | null; hasta: string | null; dias_con_movimiento: number }
  saldo: { inicial: number | null; final: number | null; creditos: number; debitos: number }
  por_categoria: { categoria: string; tipo: string; n: number; monto: number }[]
  retiros: Retiro[]
  sistema_sin_banco: SinBanco[]
  ingresos: Ingreso[]
  comisiones: { n: number; monto: number; de_retiros_rebotados: number }
  otros: Otro[]
}

const ESTADO: Record<string, { label: string; tono: 'ok' | 'warn' | 'bad' | 'mute' }> = {
  CUADRA: { label: 'Cuadra', tono: 'ok' },
  CUADRA_FECHA_DISTINTA: { label: 'Cuadra · otra fecha en sistema', tono: 'warn' },
  CUADRA_OTRA_CUENTA: { label: 'Cuadra · sistema dice otra cuenta', tono: 'warn' },
  REBOTADO: { label: 'Rebotado (con reversa)', tono: 'mute' },
  POSIBLE_PAGO_DOBLE: { label: 'Posible pago doble', tono: 'bad' },
  BANCO_SIN_SISTEMA: { label: 'Banco sin retiro en sistema', tono: 'bad' },
  OTRO_EGRESO: { label: 'Otro egreso', tono: 'mute' },
  SIN_DUENO: { label: 'Sin dueño en sistema', tono: 'bad' },
}
const TONO = {
  ok: { bg: '#ecfdf5', fg: '#047857' }, warn: { bg: '#fffbeb', fg: '#b45309' },
  bad: { bg: '#fef2f2', fg: '#b91c1c' }, mute: { bg: '#f4f4f5', fg: '#52525b' },
}
const mesActual = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guatemala' }).slice(0, 7)

function Chip({ estado }: { estado: string }) {
  const e = ESTADO[estado] ?? { label: estado, tono: 'mute' as const }
  const t = TONO[e.tono]
  return <span style={{ background: t.bg, color: t.fg, borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{e.label}</span>
}

type Tab = 'retiros' | 'sin_banco' | 'ingresos' | 'otros' | 'categorias'

export function NeoCoreConciliacionMensualPage() {
  const [cuentas, setCuentas] = useState<Cuenta[]>([])
  const [cuentaId, setCuentaId] = useState('')
  const [mes, setMes] = useState(mesActual())
  const [res, setRes] = useState<Resultado | null>(null)
  const [cargando, setCargando] = useState(false)
  const [msg, setMsg] = useState<{ tipo: 'ok' | 'err'; texto: string } | null>(null)
  const [tab, setTab] = useState<Tab>('retiros')
  const [vista, setVista] = useState<'cuenta' | 'consolidado'>('cuenta')
  const [filtro, setFiltro] = useState<string>('TODOS')
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    supabase.from('cuenta_bancaria_propia').select('id, banco, numero_cuenta, alias, activa').order('activa', { ascending: false })
      .then(({ data }) => {
        const l = (data ?? []) as Cuenta[]
        setCuentas(l)
        if (l.length && !cuentaId) setCuentaId(l[0].id)
      })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const cargar = useCallback(async () => {
    if (!cuentaId || !mes) return
    setCargando(true)
    const { data, error } = await supabase.rpc('conciliacion_mes', { p_cuenta: cuentaId, p_mes: `${mes}-01` })
    setCargando(false)
    if (error) { setMsg({ tipo: 'err', texto: error.message }); return }
    setRes(data as Resultado)
  }, [cuentaId, mes])

  useEffect(() => { void cargar() }, [cargar])

  const subir = async (file: File) => {
    setMsg(null)
    try {
      const p = await parseArchivoBancario(file)
      const cta = cuentas.find((c) => c.id === cuentaId)
      if (p.cuenta && cta && !cta.numero_cuenta.includes(p.cuenta) && !p.cuenta.includes(cta.numero_cuenta)) {
        const ok = confirm(`El archivo parece ser de la cuenta ${p.cuenta}, pero elegiste ${cta.numero_cuenta}. ¿Cargarlo igual en ${cta.numero_cuenta}?`)
        if (!ok) return
      }
      setCargando(true)
      const { data, error } = await supabase.rpc('importar_movimientos_bancarios', {
        p_cuenta: cuentaId, p_banco: p.banco, p_filename: file.name, p_rows: p.filas,
      })
      setCargando(false)
      if (error) throw new Error(error.message)
      const d = data as { insertados: number; duplicados: number }
      setMsg({ tipo: 'ok', texto: `${file.name}: ${d.insertados} movimientos nuevos · ${d.duplicados} ya estaban cargados.` })
      const meses = Array.from(new Set(p.filas.map((f) => f.fecha_hora.slice(0, 7))))
      if (meses.length === 1 && meses[0] !== mes) setMes(meses[0]); else await cargar()
    } catch (e) {
      setCargando(false)
      setMsg({ tipo: 'err', texto: e instanceof Error ? e.message : String(e) })
    } finally {
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const resumenRetiros = useMemo(() => {
    const m = new Map<string, { n: number; monto: number }>()
    for (const r of res?.retiros ?? []) {
      const x = m.get(r.estado) ?? { n: 0, monto: 0 }
      x.n++; x.monto += Number(r.monto); m.set(r.estado, x)
    }
    return Array.from(m.entries()).sort((a, b) => b[1].n - a[1].n)
  }, [res])

  const retirosFiltrados = useMemo(
    () => (res?.retiros ?? []).filter((r) => filtro === 'TODOS' || r.estado === filtro),
    [res, filtro],
  )

  const exportar = () => {
    if (!res) return
    const wb = XLSX.utils.book_new()
    const hoja = (rows: object[], nombre: string) =>
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.length ? rows : [{ sin: 'sin movimientos' }]), nombre)
    hoja([
      { concepto: 'Cuenta', valor: `${res.cuenta.banco} ${res.cuenta.numero} (${res.cuenta.contable ?? ''})` },
      { concepto: 'Mes', valor: res.mes },
      { concepto: 'Movimientos cargados', valor: res.cargado.movimientos },
      { concepto: 'Cargado desde', valor: res.cargado.desde }, { concepto: 'Cargado hasta', valor: res.cargado.hasta },
      { concepto: 'Saldo inicial (banco)', valor: res.saldo.inicial }, { concepto: 'Créditos', valor: res.saldo.creditos },
      { concepto: 'Débitos', valor: res.saldo.debitos }, { concepto: 'Saldo final (banco)', valor: res.saldo.final },
      { concepto: 'Comisiones', valor: `${res.comisiones.n} · ${res.comisiones.monto}` },
      ...resumenRetiros.map(([e, x]) => ({ concepto: `Retiros · ${ESTADO[e]?.label ?? e}`, valor: `${x.n} · ${x.monto}` })),
      { concepto: 'Retiros en sistema sin salida en banco', valor: res.sistema_sin_banco.length },
    ], 'Resumen')
    hoja(res.retiros.map((r) => ({
      estado: ESTADO[r.estado]?.label ?? r.estado, fecha_banco: r.fecha_banco, doc_banco: r.doc, monto: Number(r.monto),
      comision: r.comision ? Number(r.comision) : null, beneficiario_banco: r.beneficiario_banco, neo_id: r.neo_id,
      nombre_sistema: r.nombre_sistema, fecha_sistema: r.fecha_sistema, cuenta_salida_sistema: r.cuenta_salida_sistema,
      reversa_doc: r.reversa_doc, descripcion: r.descripcion, retiro_id: r.retiro_id,
    })), 'Retiros')
    hoja(res.sistema_sin_banco.map((s) => ({ ...s, monto: Number(s.monto) })), 'Sistema sin banco')
    hoja(res.ingresos.map((i) => ({
      estado: ESTADO[i.estado]?.label ?? i.estado, fecha_banco: i.fecha_banco, doc_banco: i.doc, monto: Number(i.monto),
      origen_nombre: i.origen_nombre, origen_cuenta: i.origen_cuenta, neo_id: i.neo_id, nombre_sistema: i.nombre,
      tipo_movimiento: i.tipo, fecha_sistema: i.fecha_sistema, descripcion: i.descripcion,
    })), 'Ingresos')
    hoja(res.otros.map((o) => ({ ...o, monto: Number(o.monto) })), 'Otros movimientos')
    hoja(res.por_categoria.map((c) => ({ ...c, monto: Number(c.monto) })), 'Por categoría')
    XLSX.writeFile(wb, `conciliacion_${res.cuenta.numero}_${res.mes}.xlsx`)
  }

  const cta = cuentas.find((c) => c.id === cuentaId)
  const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: 11, color: 'var(--muted-foreground)', fontWeight: 600, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }
  const td: React.CSSProperties = { padding: '7px 10px', fontSize: 12.5, borderBottom: '1px solid var(--border)', verticalAlign: 'top' }
  const num: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }
  const card: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 12, padding: 14, background: 'var(--card, #fff)' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Conciliación bancaria mensual</h1>
        <p style={{ color: 'var(--muted-foreground)', fontSize: 13, margin: '4px 0 0' }}>
          Subí el estado de cuenta del banco y cruzalo contra el sistema por mes. Interbanco: “EstadoDeCuenta_….xls”. BAC: “Consulta de Transferencias.csv”.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 4, background: 'var(--muted, #f4f4f5)', borderRadius: 10, padding: 4, alignSelf: 'flex-start' }}>
        {([['cuenta', 'Por cuenta'], ['consolidado', 'Consolidado del mes (todas las cuentas + contabilidad)']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setVista(k)}
            style={{ border: 0, borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', background: vista === k ? 'var(--card, #fff)' : 'transparent', boxShadow: vista === k ? '0 1px 2px rgba(0,0,0,.08)' : 'none' }}>{l}</button>
        ))}
      </div>

      {vista === 'consolidado' && (
        <>
          <input type="month" value={mes} onChange={(e) => setMes(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', alignSelf: 'flex-start' }} />
          <ConsolidadoMes mes={mes} />
        </>
      )}

      {vista === 'cuenta' && <>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)' }}>
          {cuentas.map((c) => (
            <option key={c.id} value={c.id}>{c.banco} · {c.numero_cuenta}{c.alias ? ` · ${c.alias}` : ''}{c.activa ? '' : ' (inactiva)'}</option>
          ))}
        </select>
        <input type="month" value={mes} onChange={(e) => setMes(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)' }} />
        <input ref={fileRef} type="file" accept=".xls,.csv,.html,.htm" style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void subir(f) }} />
        <button onClick={() => fileRef.current?.click()} disabled={!cuentaId || cargando}
          style={{ padding: '8px 14px', borderRadius: 8, background: 'var(--brand, #ff5b04)', color: '#fff', border: 0, fontWeight: 600, cursor: 'pointer' }}>
          Subir estado de cuenta
        </button>
        <button onClick={exportar} disabled={!res || cargando}
          style={{ padding: '8px 14px', borderRadius: 8, background: 'transparent', border: '1px solid var(--border)', fontWeight: 600, cursor: 'pointer' }}>
          Exportar Excel
        </button>
        {cargando && <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>Procesando…</span>}
      </div>

      {msg && (
        <div style={{ ...card, background: msg.tipo === 'ok' ? '#ecfdf5' : '#fef2f2', color: msg.tipo === 'ok' ? '#065f46' : '#991b1b', fontSize: 13 }}>{msg.texto}</div>
      )}

      {res && res.cargado.movimientos === 0 && (
        <div style={{ ...card, fontSize: 13 }}>
          No hay movimientos del banco cargados para {cta?.banco} {cta?.numero_cuenta} en {res.mes}. Subí el estado de cuenta de ese mes.
        </div>
      )}

      {res && res.cargado.movimientos > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
            <div style={card}><div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>Movimientos cargados</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{res.cargado.movimientos}</div>
              <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{res.cargado.desde} → {res.cargado.hasta}</div></div>
            <div style={card}><div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>Saldo inicial · final (banco)</div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{res.saldo.inicial != null ? formatQ(res.saldo.inicial) : '—'}</div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{res.saldo.final != null ? formatQ(res.saldo.final) : '—'}</div></div>
            <div style={card}><div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>Créditos · Débitos</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#047857' }}>+{formatQ(res.saldo.creditos)}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#b91c1c' }}>−{formatQ(res.saldo.debitos)}</div></div>
            <div style={card}><div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>Comisiones</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{formatQ(res.comisiones.monto)}</div>
              <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{res.comisiones.n} transferencias · {res.comisiones.de_retiros_rebotados} de rebotes</div></div>
            <div style={{ ...card, borderColor: res.sistema_sin_banco.length ? '#fecaca' : 'var(--border)' }}>
              <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>Retiros en sistema sin salida en banco</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: res.sistema_sin_banco.length ? '#b91c1c' : undefined }}>{res.sistema_sin_banco.length}</div>
              <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>hasta {res.cargado.hasta}</div></div>
          </div>

          <div style={card}>
            <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>Retiros · resultado del cruce</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {resumenRetiros.map(([e, x]) => (
                <button key={e} onClick={() => { setTab('retiros'); setFiltro(e) }}
                  style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '6px 10px', background: filtro === e ? 'var(--muted, #f4f4f5)' : 'transparent', cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Chip estado={e} /><span style={{ fontSize: 12, fontWeight: 600 }}>{x.n} · {formatQ(x.monto)}</span>
                </button>
              ))}
              {filtro !== 'TODOS' && <button onClick={() => setFiltro('TODOS')} style={{ border: 0, background: 'transparent', fontSize: 12, textDecoration: 'underline', cursor: 'pointer' }}>ver todos</button>}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 6, borderBottom: '1px solid var(--border)' }}>
            {([['retiros', `Retiros (${res.retiros.length})`], ['sin_banco', `Sistema sin banco (${res.sistema_sin_banco.length})`],
              ['ingresos', `Ingresos (${res.ingresos.length})`], ['otros', `Otros movimientos (${res.otros.length})`], ['categorias', 'Por categoría']] as [Tab, string][])
              .map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} style={{ border: 0, background: 'transparent', padding: '8px 12px', fontSize: 13, fontWeight: tab === k ? 700 : 500, borderBottom: tab === k ? '2px solid var(--brand, #ff5b04)' : '2px solid transparent', cursor: 'pointer' }}>{l}</button>
              ))}
          </div>

          <div style={{ overflowX: 'auto' }}>
            {tab === 'retiros' && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Estado</th><th style={th}>Fecha banco</th><th style={th}>Doc</th><th style={{ ...th, textAlign: 'right' }}>Monto</th><th style={{ ...th, textAlign: 'right' }}>Comisión</th><th style={th}>Beneficiario (banco)</th><th style={th}>NeoID</th><th style={th}>Asociado (sistema)</th><th style={th}>Fecha sistema</th><th style={th}>Cuenta salida sistema</th></tr></thead>
                <tbody>{retirosFiltrados.map((r, i) => (
                  <tr key={i}><td style={td}><Chip estado={r.estado} />{r.reversa_doc && <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>reversa {r.reversa_doc}</div>}</td>
                    <td style={td}>{r.fecha_banco}</td><td style={td}>{r.doc}</td><td style={num}>{formatQ(r.monto)}</td><td style={num}>{r.comision ? formatQ(r.comision) : '—'}</td>
                    <td style={td}>{r.beneficiario_banco ?? <em style={{ color: 'var(--muted-foreground)' }}>sin nombre</em>}</td><td style={td}>{r.neo_id ?? '—'}</td>
                    <td style={td}>{r.nombre_sistema ?? '—'}</td><td style={td}>{r.fecha_sistema ?? '—'}</td><td style={td}>{r.cuenta_salida_sistema ?? '—'}</td></tr>))}</tbody>
              </table>
            )}
            {tab === 'sin_banco' && (
              res.sistema_sin_banco.length === 0
                ? <p style={{ fontSize: 13, padding: 12 }}>Todos los retiros del sistema con esta cuenta de salida tienen su salida en el banco (hasta {res.cargado.hasta}).</p>
                : <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={th}>Fecha sistema</th><th style={th}>NeoID</th><th style={th}>Asociado</th><th style={{ ...th, textAlign: 'right' }}>Monto</th><th style={th}>Cuenta salida</th></tr></thead>
                  <tbody>{res.sistema_sin_banco.map((s) => (
                    <tr key={s.retiro_id}><td style={td}>{s.fecha_sistema}</td><td style={td}>{s.neo_id}</td><td style={td}>{s.nombre}</td><td style={num}>{formatQ(s.monto)}</td><td style={td}>{s.cuenta_salida_sistema}</td></tr>))}</tbody>
                </table>
            )}
            {tab === 'ingresos' && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Estado</th><th style={th}>Fecha banco</th><th style={th}>Doc / Ref</th><th style={{ ...th, textAlign: 'right' }}>Monto</th><th style={th}>Origen (banco)</th><th style={th}>Cuenta origen</th><th style={th}>NeoID</th><th style={th}>Asociado (sistema)</th><th style={th}>Tipo</th><th style={th}>Fecha sistema</th></tr></thead>
                <tbody>{res.ingresos.map((x, i) => (
                  <tr key={i}><td style={td}><Chip estado={x.estado} /></td><td style={td}>{x.fecha_banco}</td><td style={td}>{x.doc}</td><td style={num}>{formatQ(x.monto)}</td>
                    <td style={td}>{x.origen_nombre ?? x.descripcion}</td><td style={td}>{x.origen_cuenta ?? '—'}</td><td style={td}>{x.neo_id ?? '—'}</td><td style={td}>{x.nombre ?? '—'}</td><td style={td}>{x.tipo ?? '—'}</td><td style={td}>{x.fecha_sistema ?? '—'}</td></tr>))}</tbody>
              </table>
            )}
            {tab === 'otros' && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Fecha banco</th><th style={th}>Tipo</th><th style={th}>Descripción</th><th style={th}>Contraparte</th><th style={th}>Doc</th><th style={{ ...th, textAlign: 'right' }}>Monto</th></tr></thead>
                <tbody>{res.otros.map((o, i) => (
                  <tr key={i}><td style={td}>{o.fecha_banco}</td><td style={td}>{o.tipo === 'CREDITO' ? 'Entrada' : 'Salida'}</td><td style={td}>{o.descripcion}</td><td style={td}>{o.contraparte ?? '—'}</td><td style={td}>{o.doc}</td>
                    <td style={{ ...num, color: o.tipo === 'CREDITO' ? '#047857' : '#b91c1c' }}>{o.tipo === 'CREDITO' ? '+' : '−'}{formatQ(o.monto)}</td></tr>))}</tbody>
              </table>
            )}
            {tab === 'categorias' && (
              <table style={{ width: '100%', borderCollapse: 'collapse', maxWidth: 560 }}>
                <thead><tr><th style={th}>Categoría</th><th style={th}>Tipo</th><th style={{ ...th, textAlign: 'right' }}>Movimientos</th><th style={{ ...th, textAlign: 'right' }}>Monto</th></tr></thead>
                <tbody>{res.por_categoria.map((c, i) => (
                  <tr key={i}><td style={td}>{c.categoria}</td><td style={td}>{c.tipo === 'CREDITO' ? 'Entrada' : 'Salida'}</td><td style={num}>{c.n}</td><td style={num}>{formatQ(c.monto)}</td></tr>))}</tbody>
              </table>
            )}
          </div>
        </>
      )}
      </>}
    </div>
  )
}
